import type { ReactNode } from 'react';

interface SeqRow {
  /** actor name the message starts from (must be in `actors`) */
  from: string;
  /** actor name the message goes to; omit for a self-action box on `from` */
  to?: string;
  /** short label on the arrow / box */
  label: string;
  /** accent this message (the key beat) */
  highlight?: boolean;
}

export interface SequenceDiagramProps {
  /** ordered lanes, left to right */
  actors: string[];
  rows: SeqRow[];
  caption?: ReactNode;
  ariaLabel?: string;
}

const W = 760;
const MARGIN_X = 84;
const TOP = 12;
const HEAD_W = 128;
const HEAD_H = 34;
const ROW_TOP = 92;
const ROW_GAP = 58;
const NUM_X = 30;

/**
 * A UML-style sequence diagram: actor lanes with lifelines and numbered,
 * directional message arrows in time order. A row with no `to` renders a
 * self-action box on its lane. Theme-aware (tokens only); scrolls on mobile.
 */
export const SequenceDiagram = ({ actors, rows, caption, ariaLabel = 'sequence diagram' }: SequenceDiagramProps) => {
  const n = actors.length;
  const usable = W - MARGIN_X * 2;
  const laneX = (i: number) => MARGIN_X + (n <= 1 ? 0 : (usable * i) / (n - 1));
  const xOf = (name: string) => laneX(actors.indexOf(name));
  const bottom = ROW_TOP + rows.length * ROW_GAP;
  const H = bottom + 16;
  const labelW = (s: string) => s.length * 6.6 + 12;

  return (
    <figure className="my-6">
      <div className="overflow-x-auto rounded-10 border border-line bg-surface-2 p-4">
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} style={{ minWidth: 560 }}>
          <defs>
            <marker id="seq-ar" markerWidth="9" markerHeight="9" refX="7" refY="3.2" orient="auto">
              <path d="M0 0 L7 3.2 L0 6.4 Z" fill="var(--text-2)" />
            </marker>
            <marker id="seq-ar-hl" markerWidth="9" markerHeight="9" refX="7" refY="3.2" orient="auto">
              <path d="M0 0 L7 3.2 L0 6.4 Z" fill="var(--accent)" />
            </marker>
          </defs>

          {actors.map((a, i) => {
            const x = laneX(i);
            return (
              <g key={a}>
                <line x1={x} y1={TOP + HEAD_H} x2={x} y2={bottom} stroke="var(--line)" strokeDasharray="3 4" />
                <rect x={x - HEAD_W / 2} y={TOP} width={HEAD_W} height={HEAD_H} rx={8} fill="var(--surface)" stroke="var(--line)" />
                <text x={x} y={TOP + HEAD_H / 2 + 1} textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="600" fill="var(--text)">
                  {a}
                </text>
              </g>
            );
          })}

          {rows.map((r, i) => {
            const y = ROW_TOP + i * ROW_GAP;
            const fromX = xOf(r.from);
            const stroke = r.highlight ? 'var(--accent)' : 'var(--text-2)';
            const num = (
              <>
                <circle cx={NUM_X} cy={y} r={11} fill={r.highlight ? 'var(--accent)' : 'var(--surface)'} stroke={r.highlight ? 'var(--accent)' : 'var(--line)'} />
                <text x={NUM_X} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontWeight="600" fill={r.highlight ? '#fff' : 'var(--text-2)'}>
                  {i + 1}
                </text>
              </>
            );

            if (!r.to) {
              const bh = 30;
              // size the box to its label, and clamp its center so a box on an
              // edge lane (e.g. the rightmost actor) never runs past the canvas.
              const bw = Math.max(150, r.label.length * 7 + 26);
              const half = bw / 2;
              const cx = Math.min(Math.max(fromX, half + 4), W - half - 4);
              return (
                <g key={i}>
                  {num}
                  <rect x={cx - half} y={y - bh / 2} width={bw} height={bh} rx={7} fill="var(--surface)" stroke={stroke} />
                  <text x={cx} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="12.5" fill={stroke}>
                    {r.label}
                  </text>
                </g>
              );
            }

            const toX = xOf(r.to);
            const dir = toX > fromX ? 1 : -1;
            const x1 = fromX + dir * 4;
            const x2 = toX - dir * 6;
            const midX = (fromX + toX) / 2;
            const lw = labelW(r.label);
            return (
              <g key={i}>
                {num}
                <rect x={midX - lw / 2} y={y - 17} width={lw} height={16} fill="var(--surface-2)" />
                <text x={midX} y={y - 12} textAnchor="middle" fontSize="12.5" fontWeight={r.highlight ? 600 : 400} fill={stroke}>
                  {r.label}
                </text>
                <line x1={x1} y1={y} x2={x2} y2={y} stroke={stroke} strokeWidth={1.5} markerEnd={r.highlight ? 'url(#seq-ar-hl)' : 'url(#seq-ar)'} />
              </g>
            );
          })}
        </svg>
      </div>
      {caption ? <figcaption className="mt-2 text-center text-13 text-text-3">{caption}</figcaption> : null}
    </figure>
  );
};
